import React from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb,
  Target,
  TrendingUp,
  Users,
  LineChart,
  Rocket,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Target,
    title: 'Stefnumótun',
    description: 'Við hjálpum þér að móta skýra stafræna stefnu fyrir þitt fyrirtæki.'
  },
  {
    icon: TrendingUp,
    title: 'Markaðsgreining',
    description: 'Ítarleg greining á markaðnum og samkeppnisaðilum.'
  },
  {
    icon: Users,
    title: 'Notendaupplifun',
    description: 'Ráðgjöf um bestu leiðir til að bæta notendaupplifun.'
  },
  {
    icon: LineChart,
    title: 'Árangursmælingar',
    description: 'Skilgreining á lykilmælikvörðum og eftirfylgni.'
  },
  {
    icon: Rocket,
    title: 'Stafræn umbreyting',
    description: 'Aðstoð við stafræna umbreytingu fyrirtækja.'
  },
  {
    icon: Lightbulb,
    title: 'Nýsköpun',
    description: 'Ráðgjöf um nýsköpun og tækifæri í stafrænni þróun.'
  }
];

const Consulting = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Fagleg ráðgjöf fyrir stafræna framtíð
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við hjálpum fyrirtækjum að ná árangri í stafrænum heimi með 
              sérfræðiþekkingu og reynslu.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={containerVariants}
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá ráðgjöf
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Alhliða ráðgjöf fyrir þitt fyrirtæki
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á fjölbreytta ráðgjöf sem hjálpar þér að ná markmiðum þínum
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="bg-gray-50 rounded-xl p-8"
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Process Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            variants={itemVariants}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ráðgjafaferlið okkar
            </h2>
            <p className="text-xl text-gray-600">
              Við fylgjum skilvirku ferli til að tryggja árangursríka ráðgjöf
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants}>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#EA295A] text-white font-bold">
                      1
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Greining á stöðu</h3>
                    <p className="text-gray-600">
                      Við byrjum á að greina núverandi stöðu og skilgreina helstu áskoranir 
                      og tækifæri.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#EA295A] text-white font-bold">
                      2
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Markmiðasetning</h3>
                    <p className="text-gray-600">
                      Við setjum skýr og mælanleg markmið fyrir verkefnið.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#EA295A] text-white font-bold">
                      3
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Aðgerðaáætlun</h3>
                    <p className="text-gray-600">
                      Við útbúum ítarlega aðgerðaáætlun með skýrum skrefum og tímalínu.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#EA295A] text-white font-bold">
                      4
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Innleiðing og eftirfylgni</h3>
                    <p className="text-gray-600">
                      Við aðstoðum við innleiðingu og fylgjum verkefninu eftir til að 
                      tryggja árangur.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="relative">
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80"
                alt="Consulting process"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#EA295A]/10 to-transparent" />
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              variants={itemVariants}
              className="text-3xl font-bold text-white mb-6"
            >
              Viltu ræða þitt verkefni?
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Við erum tilbúin að hjálpa þér að ná þínum markmiðum með faglegri 
              ráðgjöf og reynslu
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link to="/hafa-samband">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Bóka fund
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Consulting;